import React, { useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { cn } from '@duck/ui'
import { User } from 'lucide-react'

interface UserAvatarProps {
    size?: 'sm' | 'md' | 'lg'
    className?: string
    /**
     * Optional user to render an avatar for.
     * When not provided, the authenticated user from `useAuth` is used.
     */
    user?: {
        avatar?: string | null
        firstName?: string | null
        lastName?: string | null
    } | null
}

const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
}

const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
}

export const UserAvatar: React.FC<UserAvatarProps> = ({
    size = 'md',
    className = '',
    user: providedUser,
}) => {
    const { user: authUser, initials: authInitials } = useAuth()

    const user = providedUser ?? authUser
    const avatar = user?.avatar
    const [imageError, setImageError] = useState(false)

    const computedInitials =
        providedUser
            ? `${providedUser.firstName?.charAt(0) ?? ''}${providedUser.lastName?.charAt(0) ?? ''}`.trim() ||
            undefined
            : authInitials

    useEffect(() => {
        setImageError(false)
    }, [avatar])

    if (avatar && !imageError) {
        return (
            <img
                src={avatar}
                alt={computedInitials || ''}
                onError={() => setImageError(true)}
                className={cn(
                    sizeClasses[size],
                    'rounded-full object-cover border-[3px] border-primary',
                    className
                )}
            />
        )
    }

    // Show User icon as fallback when image fails or no avatar
    return (
        <div
            className={cn(
                sizeClasses[size],
                'rounded-full bg-accent/80 flex items-center justify-center font-medium text-accent-foreground border border-primary',
                className
            )}
        >
            {computedInitials ? (
                computedInitials
            ) : (
                <User className={iconSizeClasses[size]} />
            )}
        </div>
    )
}

